import React, { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { X, Rotate3D, Maximize2 } from 'lucide-react';
import useModalA11y from '../hooks/useModalA11y';
import { trackStore360 } from '../utils/analytics';
import { isWebGLAvailable } from '../utils/panorama';

// A WebGL-nézegető KÜLÖN CHUNK: az első oldalbetöltéskor egyetlen bájtja sem
// töltődik le, csak a „Virtuális bejárás” gombra kattintva.
const Store360Canvas = lazy(() => import('./Store360Canvas'));

/**
 * 360° üzletbejárás – preview kártya + teljes képernyős nézegető.
 *
 * Alapállapotban CSAK a preview kép, a 360°-jelvény és a gomb látszik; sem a
 * panoráma (több MB), sem a viewer JS nem töltődik be. A modál a
 * `useModalA11y` hookot használja (fókusz-csapda, ESC, body-zár) – ugyanaz,
 * mint a foglalási modálnál, tehát nincs második megoldás ugyanarra.
 */
export default function Store360Viewer({
  title,
  description,
  previewImage,
  panoramaImage,
  storeId,
  storeName,
  className = '',
  // 'card'   – előnézet-kártya + gomb (alap; a StoreGallery ezt használja).
  // 'button' – CSAK a nyitógomb, a hívó stílusával. A főoldali StoreShowcase
  //            saját kártyát rajzol (külső fotó + cím + cím + leírás); oda a
  //            viewer saját előnézet-kártyája MÁSODIK képet és MÁSODIK gombot
  //            tenne. A modál, a lazy chunk és a mérés mindkét módban ugyanaz –
  //            nincs második viewer-implementáció.
  variant = 'card',
  triggerLabel = 'Virtuális bejárás',
  triggerClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const dialogRef = useRef(null);
  const openerRef = useRef(null);

  // Panoráma nélkül a komponens EGYÁLTALÁN nem jelenik meg (nincs placeholder).
  const hasPanorama = Boolean(panoramaImage);

  const close = useCallback(() => {
    setOpen(false);
    trackStore360('close', { storeId, storeName });
    // A fókusz visszakerül a megnyitó gombra – billentyűzetes használatnál
    // különben az oldal tetejére ugrana.
    openerRef.current?.focus();
  }, [storeId, storeName]);

  useModalA11y(dialogRef, { active: open, onEscape: close });

  const handleOpen = () => {
    if (!isWebGLAvailable()) {
      setFailed(true);
      trackStore360('error', { storeId, storeName, reason: 'no-webgl' });
      return;
    }
    setOpen(true);
    trackStore360('open', { storeId, storeName });
  };

  const handleLoaded = useCallback((info) => {
    trackStore360('loaded', { storeId, storeName, loadTimeMs: info?.loadTimeMs });
  }, [storeId, storeName]);

  const handleError = useCallback((reason) => {
    setFailed(true);
    setOpen(false);
    trackStore360('error', { storeId, storeName, reason });
  }, [storeId, storeName]);

  if (!hasPanorama) return null;

  // A teljes képernyős nézegető – MINDKÉT variáns ugyanezt rendereli.
  const dialog = open ? (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`360 fokos bejárás: ${storeName || title}`}
      // z-index: a mobil CTA-sáv z-[9990] – a teljes képernyős bejárásnak
      // FÖLÉ kell kerülnie, különben a sáv rátakar a panorámára.
      className="fixed inset-0 z-[9999] bg-black"
      data-testid="store360-dialog"
    >
      <Suspense
        fallback={
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
            Nézegető betöltése…
          </div>
        }
      >
        <Store360Canvas
          panoramaImage={panoramaImage}
          onLoaded={handleLoaded}
          onError={handleError}
          alt={`${storeName || title} – 360 fokos panoráma`}
        />
      </Suspense>

      <button
        type="button"
        onClick={close}
        aria-label="Bejárás bezárása"
        data-testid="store360-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/90 text-ink flex items-center justify-center hover:bg-white transition-colors"
      >
        <X size={22} />
      </button>

      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-[11px] leading-snug text-center max-w-[92vw]">
        Húzd az ujjaddal vagy az egérrel · nagyítás: csippentés / görgő · bezárás: ESC
      </p>
    </div>
  ) : null;

  // ── 'button' variáns: csak a trigger, a hívó kártyáján belül ───────────────
  if (variant === 'button') {
    return (
      <>
        {failed ? (
          <p className="text-sm text-gray-600" role="status">
            A 360° bejárás jelenleg nem érhető el.
          </p>
        ) : (
          <button
            type="button"
            ref={openerRef}
            onClick={handleOpen}
            aria-label={`${triggerLabel} indítása – ${storeName || title}`}
            aria-haspopup="dialog"
            data-testid={`store360-open-${storeId}`}
            className={triggerClassName}
          >
            <Rotate3D size={18} aria-hidden="true" /> {triggerLabel}
          </button>
        )}
        {dialog}
      </>
    );
  }

  return (
    <div className={className} data-testid={`store360-${storeId}`}>
      {/* ── Preview kártya: NINCS viewer, NINCS panoráma-letöltés ──────────── */}
      <div className="relative rounded-2xl overflow-hidden border border-gray-100 bg-gray-50">
        {previewImage ? (
          <img
            src={previewImage}
            alt={`${title} – 360 fokos körkép előnézete`}
            loading="lazy"
            decoding="async"
            width="1280"
            height="720"
            className="w-full aspect-video object-cover"
          />
        ) : (
          <div className="w-full aspect-video bg-ink/5 flex items-center justify-center">
            <Rotate3D size={32} className="text-brand" aria-hidden="true" />
          </div>
        )}

        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ink/80 text-white text-xs font-bold">
          <Rotate3D size={13} aria-hidden="true" /> 360°
        </span>

        <div className="p-4">
          <h3 className="font-bold text-ink">{title}</h3>
          {description && <p className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</p>}

          {failed ? (
            <p className="mt-3 text-sm text-gray-500" role="status">
              A 360° bejárás jelenleg nem érhető el.
            </p>
          ) : (
            <button
              type="button"
              ref={openerRef}
              onClick={handleOpen}
              aria-label={`Virtuális bejárás indítása – ${storeName || title}`}
              data-testid={`store360-open-${storeId}`}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white font-semibold rounded-full hover:bg-brand-hover transition-colors"
            >
              <Maximize2 size={16} aria-hidden="true" /> Virtuális bejárás
            </button>
          )}
        </div>
      </div>

      {dialog}
    </div>
  );
}
